<script setup>
import TitleField from './TitleField.vue';
import TextEditor from '/app/components/form/TextEditor.vue';
import WordsRestriction from './WordsRestriction.vue';
import AssistantButton from '/app/components/challenge/AssistantButton.vue';
import get from 'lodash/get';
import { useDetails, categoriesWithRestrictWords } from '/app/composables/challenge';
import { restrictedWords } from '/app/composables/challenge-create-flow';
import { useBus } from '/app/composables/bus';
import { useFormContext } from '/app/composables/form';
import { ref, computed, watch, nextTick, inject, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import NBubble from '/app/components/NBubble.vue';
import NExperiencePoints from '/app/components/game/brain/ExperiencePoints.vue';
import NTags from '/app/components/form/Tags.vue';

const context = inject('context');
const stepper = inject('stepper');
const builder = inject('builder');

const form = useFormContext();
const challengeRef = computed(() => form.fields.challenge);

const { isCaption } = useDetails(challengeRef);

const { t } = useI18n();
const { on } = useBus();

const textEditor = useTemplateRef('textEditor');
const wordsRestriction = useTemplateRef('wordsRestriction');

const hasRestrictedWords = ref(null);

const challenge = computed(() => form.fields.challenge);

const categoriesBreadcrumbs = computed(() =>
    builder.categoryTree?.map((category) => category.name) || [],
);

const suffix = computed(() => {
    const category = builder.categoryTree[0];
    let s = category ? category.slug : '*';

    s = isCaption.value ? (challenge.value.caption_solution_format || 'caption') : '*';
    s = ['social-issues', 'validate', 'make-better'].find((c) => s.includes(c)) || s;

    return s;
});

const title = computed(() => {
    const category = builder.category;

    return t(`challenges.subheads.question-title.${suffix.value}`, {
        category: category && category.name !== 'Caption' ? category.name : 'Funny',
    });
});

const description = computed(() => t(`challenges.intros.question-description.${suffix.value}`));

const shouldCheckRestricWords = computed(
    () => !!challenge.value.solution_format
        || categoriesWithRestrictWords.some(
            (category) => context.categoryMain && context.categoryMain.reference.includes(category),
        ),
);

function resetTextEditor() {
    if (textEditor.value) {
        textEditor.value.reset();
    }
}

function setInitialTitle(force) {
    nextTick(() => {
        const hasCaption = get(context, 'categoryMain.slug', '').includes('caption');

        if (force || !challenge.value.question_title) {
            challenge.value.question_title = 'What could';
        }

        if (isCaption.value || hasCaption) {
            challenge.value.question_title = title.value;
            challenge.value.question_detail = null;
        }
    });
}

function questionTitleChanged() {
    if (shouldCheckRestricWords.value) {
        wordsRestriction.value && wordsRestriction.value.notify();
    }

    // featureFlag.ready(async ({ enabled }) => {
    //     if (!enabled('categorify-challenge')) return;
    //
    //     const { data: response } = await api.challenges.categorify({
    //         title: challenge.value.question_title,
    //     });
    //
    //     const { secondary_category } = response.data;
    //
    //     if (secondary_category) {
    //         challenge.value.platform_category = secondary_category.id;
    //     }
    //
    //     nextTick(() => {
    //         if (isCaption.value && stepper) {
    //             stepper.to('image');
    //         }
    //     });
    // });
}

function handleIdeaSuggestion(data) {
    challenge.value.question_title = data.question_title;
    challenge.value.question_detail = data.question_details;
    challenge.value.tags = [
        ...challenge.value.tags,
        ...(data.tags || []),
    ];
}

watch(hasRestrictedWords, (yes) => {
    if (yes === false) {
        form.errors.set(
            'question_title',
            'Consider using one of the words listed below, or similar!',
        );
    } else {
        form.errors.clear('question_title');
    }
});

on('challenge:creation-flow:reseted', () => {
    setInitialTitle();
    resetTextEditor();
});

on('challenge:creation-flow:edition-started', () => {
    setInitialTitle();
});
</script>

<template>
    <div>
        <div class="challenge-section-heading">
            <h2 class="challenge-section-heading__title">{{ title }}</h2>
            <div class="flex gap-4 items-center">
                <p class="challenge-section-heading__description">{{ description }}</p>

				<AssistantButton
					tool="suggest-challenge"
					:params="{
						categories: categoriesBreadcrumbs.filter(Boolean).join(','),
                        mandatory_keywords: restrictedWords.join(','),
					}"
					@success="handleIdeaSuggestion"
				/>
            </div>
        </div>
        <div class="form-section-wrapper">
            <div class="form-groups">
                <TitleField
                    v-model="challenge.question_title"
                    :initial-value="challenge.question_title"
                    :force-error="shouldCheckRestricWords && hasRestrictedWords === false"
                    :disabled="isCaption"
                    @blur="questionTitleChanged"
                    @update:model-value="hasRestrictedWords = null"
                />

                <WordsRestriction
                    v-if="!challenge.co_author && shouldCheckRestricWords"
                    :modelValue="challenge.question_title"
                    :challenge="challenge"
                    v-model:valid="hasRestrictedWords"
                    ref="wordsRestriction"
                    class="mt-0"
                />

                <div class="form-group relative">

                    <div class="absolute z-[1] -top-10 sm:left-80 sm:-top-0.5 md:left-110">
                        <NExperiencePoints fqdn="catalyst.ChallengePublished.CreatedChallenge" #default="{ points }">
                            <NBubble v-if="!!points && challenge.question_detail" color="catalyst" :animated="true">
                                +{{ points }} Neurons
                            </NBubble>
                        </NExperiencePoints>
                    </div>

                    <div class="quill-editor-wrapper placeholder-gray-100">
                        <div class="quill-editor-title">Describe the details of your Question</div>
                        <TextEditor
                            name="question_detail"
                            ref="textEditor"
                            v-model="challenge.question_detail"
                            :defaultValue="challenge.question_detail"
                            :disabled="isCaption"
                            placeholder="Type or paste here. You can add styling with the formatting buttons above."
                            tabindex="2"
                        />
                    </div>
                    <div class="flex items-center justify-between w-full py-2">
                        <p class="form-block-help-text has-margin-top has-no-margin-bottom">
                            Be descriptive and concise.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="! isCaption" class="form-section-wrapper">
            <div class="form-groups">
                <div class="form-group">
                    <h2 v-if="context && context.categoryMain" class="form-group__title">
                        Main Category:
                        <span class="tag is-white">{{ context.categoryMain.name }}</span>
                    </h2>
                    <label class="form-group__label">
                        <b>Secondary Tags</b>
                        <i class="font-weight-normal">(optional)</i>
                    </label>
                    <NTags
                        v-model="form.fields.challenge.tags"
                        tabindex="3"
                    />
                    <p class="form-block-help-text mt-3 mb-0">
                        The more categories/keywords you add, the easier your challenge can be discovered across BigNerve. Separate keywords by "comma" or "return".
                    </p>
                </div>
            </div>
        </div>
        <div class="info-box">
            <span aria-hidden="true" class="info-box__icon icon-exclamation-circle-solid"></span>
            <p class="info-box__content">
                <b>Why do we use “could”?</b>  We use “What could...” or “How could...” in all questions to help focus the innovators on new ideas and future options. Our site is a possibility generator, not a “question &amp; answer” site for expertise or proven solutions, or for polls. That's why we use the subjunctive tense. Ideas in our community are about <b>unexpected options</b>, not "what is" or "how can I".
            </p>
        </div>
    </div>
</template>
